/**
 * PlayerSearchOverlay - persistent player search modal.
 *
 * Opens from the dashboard header search icon (Session C-B §2.3).
 * Typeahead against GET /api/players?search=... - reuse existing
 * endpoint if it exists, otherwise falls back to /api/players with
 * client-side filtering.
 *
 * Selecting a player fetches the pre-computed analytics canvas from
 * the DB (per PGF_KNOWN_GOOD_LEDGER.md KG-11 - presentation reads
 * pre-computed data, never computes at card-open) and passes the
 * full payload to the presentational PlayerAnalysisCanvas.
 */
import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";
import PlayerAnalysisCanvas from "../analytics/PlayerAnalysisCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlayerSearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  // [B1-α.A] Canvas payload for the selected player - fetched from
  // /canvas so the presentational component receives real axes rather
  // than only a bare player_id (which triggered "Radar data pending").
  const [canvasData, setCanvasData] = useState(null);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [canvasError, setCanvasError] = useState(null);
  const inputRef = useRef(null);
  const debTimer = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) {
      setQ(""); setResults([]); setSelected(null);
      setCanvasData(null); setCanvasError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !q || q.length < 2) { setResults([]); return; }
    clearTimeout(debTimer.current);
    debTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/players`, {
          headers: authHeaders(),
          params: { search: q, limit: 12 },
        });
        setResults(Array.isArray(data) ? data : data?.items || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debTimer.current);
  }, [q, open]);

  // [B1-α.A] When a player is selected, fetch the pre-computed canvas
  // row from the DB before mounting the presentational component.
  useEffect(() => {
    if (!selected?.id) { setCanvasData(null); setCanvasError(null); return; }
    let cancelled = false;
    setCanvasLoading(true);
    setCanvasError(null);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/analytics/players/${selected.id}/canvas`,
          {
            headers: authHeaders(),
            params: { season_year: 2024, scoring_format: "half_ppr" },
          },
        );
        if (!cancelled) setCanvasData(data);
      } catch (err) {
        if (!cancelled) {
          setCanvasError(
            err?.response?.status === 404
              ? "No pre-computed analytics on file for this player yet."
              : "Analytics temporarily unavailable.",
          );
          setCanvasData(null);
        }
      } finally {
        if (!cancelled) setCanvasLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-20 p-4"
      onClick={() => (selected ? setSelected(null) : onClose?.())}
      data-testid="player-search-overlay"
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-[#181818] border border-[#2A2A2A] overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {selected ? (
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A]">
              <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">
                Player Analytics
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-[#A0A0A0] hover:text-white"
                data-testid="player-analytics-back"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <PlayerAnalysisCanvas
                player={canvasData?.player || selected}
                pgfCanvas={canvasData}
                loading={canvasLoading}
                error={canvasError}
                data-testid="player-search-canvas"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2A2A2A]">
              <Search size={16} className="text-[#A0A0A0]" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search any NFL player…"
                className="flex-1 bg-transparent border-0 outline-none text-white text-sm placeholder-[#606060]"
                data-testid="player-search-input"
              />
              <button
                type="button"
                onClick={onClose}
                className="text-[#A0A0A0] hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loading && <div className="text-center py-6 text-[#A0A0A0] text-sm">Searching…</div>}
              {!loading && q.length >= 2 && results.length === 0 && (
                <div className="text-center py-6 text-[#A0A0A0] text-sm">
                  No players match &ldquo;{q}&rdquo;.
                </div>
              )}
              {!loading && q.length < 2 && (
                <div className="text-center py-6 text-[#606060] text-xs">
                  Type at least 2 characters to search.
                </div>
              )}
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelected(p)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#FF6B00]/10 text-left"
                  data-testid={`player-search-result-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase text-xs tracking-wide text-[#F5F5F5] truncate">
                      {p.name}
                    </div>
                    <div className="text-[10px] text-[#A0A0A0]">
                      {p.position} · {p.nfl_team || "FA"}
                      {p.bye_week ? ` · BYE ${p.bye_week}` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
